import { PageController } from '@client/components/_core/layout/PageController';
import { PageLayoutHeader } from '@client/components/_core/layout/PageLayout/PageLayoutHeader';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { SurfaceCard } from '@client/components/_ui/display/SurfaceCard';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { Link } from '@client/components/Common/Link';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { createClientColumnHelper, PrismaTable, useClientTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck, Target } from 'lucide-react';
import { useMemo } from 'react';
import type { Metric } from '../~dashboard/sections/Claims/Fleet/fleet-data';
import { type SlaRow, type SlaRowStatus, slaRows, slaSummary } from './fleet-portal-demo-data';
import { PortalMetricRow, PortalMetricTile } from './PortalMetrics';
import { PortalScopeSelect, usePortalScope } from './usePortalScope';

export const Route = createFileRoute('/console/$orgId/fleet-portal/sla')({
    staticData: {
        ui: {
            title: 'SLA and KPIs',
            subtitle: 'Performance against your contractual service commitments',
            icon: Target,
        },
    },
    component: FleetPortalSlaPage,
});

function SlaStatusTag({ status }: { status: SlaRowStatus }) {
    return <StatusBadge module="fleetSla" status={status} />;
}

const slaColumnHelper = createClientColumnHelper<SlaRow>();

const slaColumns = [
    slaColumnHelper.accessor('metric', { id: 'metric', header: 'Metric', className: 'font-medium' }),
    slaColumnHelper.accessor('target', { id: 'target', header: 'Target' }),
    slaColumnHelper.accessor('actual', { id: 'actual', header: 'Actual', align: 'right', className: 'font-semibold' }),
    slaColumnHelper.accessor('status', {
        id: 'status',
        header: 'Status',
        className: 'whitespace-nowrap',
        cell: ({ row }) => <SlaStatusTag status={row.status} />,
    }),
    slaColumnHelper.accessor('note', { id: 'note', header: 'Note' }),
];

/** The one row computed from real data — CUR-534. The rest stay demo copy. */
const LIVE_METRIC = 'Repairer first contact';

type SlaQueryData = RouterOutputs['fleetPortal']['getRepairerFirstContactSla'];

/**
 * Replaces the demo values on the live row with the computed result, leaving
 * every other row untouched. The row keeps its position in the contractual
 * list so the grid still reads as the client's full SLA schedule.
 */
function withLiveRow(sla: SlaQueryData | undefined): SlaRow[] {
    if (!sla) {
        return slaRows;
    }
    return slaRows.map((row) => {
        if (row.metric !== LIVE_METRIC) {
            return row;
        }
        if (sla.metPercentage === null) {
            // No claim has reached a verdict yet: either none has an AI damage
            // scan, or every scanned claim is still inside its target window.
            return {
                ...row,
                actual: '—',
                status: 'ok',
                note: sla.isTruncated
                    ? 'No claims have reached this target in the latest 500 matching claims'
                    : 'No claims have reached this target yet',
            };
        }
        const breachCount = sla.breaches.length;
        return {
            ...row,
            actual: `${sla.metPercentage}%`,
            // 90% is the contractual commitment shown in the Target column.
            status: sla.metPercentage >= 90 ? 'ok' : 'watch',
            note:
                breachCount === 0
                    ? `All ${sla.measuredClaims} measured claim(s) within target${sla.isTruncated ? ' (latest 500 only)' : ''}`
                    : `${breachCount} claim(s) outside target — expand to review${sla.isTruncated ? ' (latest 500 only)' : ''}`,
        };
    });
}

/** The breach list shown when the live row is expanded. */
function BreachList({ sla }: { sla: SlaQueryData }) {
    const { formatDate } = useFormatter();
    const orgId = useOrgId();

    if (sla.breaches.length === 0) {
        return <div className="p-3 text-muted-foreground text-sm">No claims are outside this target.</div>;
    }

    return (
        <div className="p-3">
            <div className="mb-2 font-medium text-sm">Claims outside target</div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-muted-foreground">
                            <th className="py-1 pr-4 font-normal">Claim</th>
                            <th className="py-1 pr-4 font-normal">Registration</th>
                            <th className="py-1 pr-4 font-normal">Estimate received</th>
                            <th className="py-1 pr-4 font-normal">Repairer engaged</th>
                            <th className="py-1 pr-4 text-right font-normal">Business days</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sla.breaches.map((breach) => (
                            <tr key={breach.claimId} className="border-separator border-t">
                                <td className="py-1.5 pr-4">
                                    <Link
                                        to="/console/$orgId/fleet-portal/claims/details/$claimId"
                                        params={{ orgId, claimId: BigInt(breach.claimId) }}
                                    >
                                        {breach.claimNumber}
                                    </Link>
                                </td>
                                <td className="py-1.5 pr-4">{breach.registration ?? '—'}</td>
                                <td className="py-1.5 pr-4">{formatDate(breach.estimateReceivedAt)}</td>
                                <td className="py-1.5 pr-4">
                                    {breach.repairerEngagedAt ? (
                                        formatDate(breach.repairerEngagedAt)
                                    ) : (
                                        <span className="text-muted-foreground">Still waiting</span>
                                    )}
                                </td>
                                <td className="py-1.5 pr-4 text-right font-medium">{breach.businessDaysElapsed}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function FleetPortalSlaPage() {
    return (
        <PageController route={Route}>
            <SlaContent />
        </PageController>
    );
}

function SlaContent() {
    const { trpc } = useTrpc();
    const scope = usePortalScope();
    const statsQuery = trpc.fleetPortal.getDashboardStats.useQuery(
        { fleetClientId: scope.fleetClientId },
        { enabled: !scope.isLoading && !scope.error },
    );
    const slaQuery = trpc.fleetPortal.getRepairerFirstContactSla.useQuery(
        { fleetClientId: scope.fleetClientId },
        { enabled: !scope.isLoading && !scope.error },
    );

    const sla = slaQuery.data;
    const rows = useMemo(() => withLiveRow(sla), [sla]);

    const tableProps = useClientTable({
        data: rows,
        columns: slaColumns,
        rowKey: 'metric',
        pagination: false,
    });

    if (scope.error) {
        return (
            <div>
                <PageLayoutHeader />
                <InlineQueryError error={scope.error} onRetry={scope.refetch} />
            </div>
        );
    }

    return (
        <div>
            <PageLayoutHeader rightAddon={<PortalScopeSelect />} />

            {statsQuery.error ? (
                <InlineQueryError error={statsQuery.error} onRetry={() => void statsQuery.refetch()} />
            ) : statsQuery.isPending || scope.isLoading ? (
                <Loading className="min-h-32" />
            ) : (
                <SlaKpiTiles
                    avgCycleTimeDays={statsQuery.data.avgCycleTimeDays}
                    atRiskClaims={statsQuery.data.atRiskClaims}
                />
            )}

            <div className="mt-4">
                <SurfaceCard
                    icon={ShieldCheck}
                    title="Service level agreement"
                    subtitle="Claims whose estimate arrived this report month — demo data except repairer first contact, cycle time and at-risk claims"
                    padded={false}
                >
                    {slaQuery.error ? (
                        <div className="p-4">
                            <InlineQueryError error={slaQuery.error} onRetry={() => void slaQuery.refetch()} />
                        </div>
                    ) : slaQuery.isPending || scope.isLoading ? (
                        <Loading className="min-h-32" />
                    ) : (
                        <PrismaTable
                            {...tableProps}
                            expandable={{
                                // Only the live row has anything to show;
                                // the demo rows have no underlying claims.
                                rowExpandable: (row: SlaRow) => row.metric === LIVE_METRIC,
                                expandedRowRender: () => <BreachList sla={slaQuery.data} />,
                            }}
                        />
                    )}
                </SurfaceCard>
            </div>
        </div>
    );
}

function SlaKpiTiles({ avgCycleTimeDays, atRiskClaims }: { avgCycleTimeDays: number | null; atRiskClaims: number }) {
    const metrics: Metric[] = [
        {
            label: 'Claims on track',
            value: `${slaSummary.claimsOnTrackPct}%`,
            valueTone: 'success',
            note: 'against SLA commitments',
        },
        {
            // CUR-504: live — claims carrying the derived "Quote overdue" flag.
            label: 'At-risk claims',
            value: String(atRiskClaims),
            valueTone: atRiskClaims > 0 ? 'warning' : 'default',
            note: 'quote overdue ≥48 hours',
        },
        { label: 'Auto-interventions', value: String(slaSummary.autoInterventions7d), note: 'last 7 days' },
        {
            label: 'Avg cycle time',
            value: avgCycleTimeDays === null ? '—' : avgCycleTimeDays.toFixed(1),
            unit: avgCycleTimeDays === null ? undefined : 'days',
            note: 'target ≤60 days',
        },
    ];

    return (
        <PortalMetricRow>
            {metrics.map((metric) => (
                <PortalMetricTile key={metric.label} metric={metric} />
            ))}
        </PortalMetricRow>
    );
}
